import lodash from 'lodash';
import { useEffect } from 'react';
import { $Formutil, Form } from 'react-antd-formutil';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import Debug from 'components/Debug';
import ScrollTop from 'components/ScrollTop';
import Toast from 'components/Toast';
import { useStorageData } from 'hooks/useStorageData';
import { PARTIAL_ACATS_BASE_PATH } from './helper';
import store from './store';

export enum PageIdEnum {
    BROKER = 'broker',
    CHECK = 'check',
    PREVIEW = 'preview',
    CHOOSE_TRANSFER_TYPE = 'chooseTransferType',
    RESULT = 'result',
    PARTIAL_DETAILS = 'partialDetails',
    FILL_CASH = 'fillCash',
    FILL_STOCK = 'fillStock',
    FILL_OPTION = 'fillOption',
    FILL_FUND = 'fillFund',
    // 编辑模式下的路由
    EDIT_BROKER = 'editBroker',
    EDIT_CHECK = 'editCheck'
}

interface ConfigType {
    id: PageIdEnum;
    basePath: typeof PARTIAL_ACATS_BASE_PATH; // 模块的基础路径, 用于跳转到模块首页
    effect?: PageIdEnum | PageIdEnum[]; // 受影响模块，当前模块修改后，则会将受影响的模块数据置空
    effectExcept?: PageIdEnum | PageIdEnum[]; // 除了effectExcept内的模块不受影响, 别的模块都受影响
    ensure?: PageIdEnum | PageIdEnum[]; // 依赖模块，该模块数据必须有，否则会跳转到模块首页，为数组时，注意填写顺序
    showProgressBar?: boolean; // 是否展示进度条, 如果不展示, 则不需要传step
    step?: number; // 如果当前页面不展示进度条, 是可以不要step的
    cache?: boolean; // 是否要缓存当前页面的值, 默认为true
}

export interface TransferFormProps<Fields = any, Validators = any, WeakFields = Fields> {
    $formutil: $Formutil<Fields, Validators, WeakFields>;
    onSubmit(
        nextFunc: (() => any) | string,
        options?: {
            effect?: ConfigType['effect'];
            effectExcept?: ConfigType['effectExcept'];
        }
    ): Promise<void>;
    getStorageDataByKey: <K = any>(pageId: PageIdEnum) => K;
    clearStorageData: () => void;
    setStorageDataByKey: <K = any>(pageId: PageIdEnum, data: K) => void;
    goNextPath(nextPath: string): void; // 统一管理页面的下一步跳转行为, 如果后续有新的要求, 可以统一处理
}

function createTransferForm(config: ConfigType): HOC<TransferFormProps> {
    const {
        id: pageId,
        step,
        showProgressBar,
        ensure,
        basePath,
        effect: configEffect,
        effectExcept: configEffectExcept,
        cache = true
    } = config;

    return function withTransferForm(WrappedComponent: any): any {
        function TransferStepForm(transferStepProps: any) {
            const navigate = useNavigate();
            const location = useLocation();
            const { getStorageDataByKey, setStorageDataByKey, getTotalStorageData, clearStorageData } =
                useStorageData<Record<PageIdEnum, any>>();
            const noFlatStorageData = getTotalStorageData({ noFlat: true });
            const goNextPath: TransferFormProps['goNextPath'] = nextPath => {
                navigate({
                    ...location,
                    pathname: nextPath
                });
            };

            useEffect(() => {
                if (typeof showProgressBar !== 'undefined') {
                    store.setShowProgressBar(showProgressBar);
                }

                if (step) {
                    store.updateStep(step);
                }
            }, []);

            // 到该步骤所需的数据是否有遗漏，如有遗漏，则跳转至当前功能模块首页
            if (ensure) {
                const missing = Array.isArray(ensure)
                    ? ensure.some(pageIdItem => {
                          // 如果是通过effect删除了某一项数据，那么该模块数据为空对象
                          // 因此也要判断是空对象的情况

                          return lodash.isEmpty(getStorageDataByKey(pageIdItem));
                      })
                    : lodash.isEmpty(getStorageDataByKey(ensure));

                if (missing) {
                    return (
                        <Navigate
                            replace
                            state={location.state}
                            to={{
                                ...location,
                                pathname: basePath
                            }}
                        />
                    );
                }
            }

            return (
                <Form
                    $defaultValues={getStorageDataByKey(pageId)}
                    render={$formutil => {
                        const handleSubmit: TransferFormProps['onSubmit'] = async (next, submitOptions) => {
                            const { $invalid, $batchState, $getFirstError, $params } = $formutil.$new();

                            if ($invalid) {
                                $batchState({
                                    $dirty: true,
                                    $touched: true
                                });

                                Toast.show($getFirstError());
                            } else {
                                // 如果需要做缓存处理，就将该页面表单值存进values
                                // 这里用JSON处理，是深拷贝的取巧用法。
                                // 深拷贝是为了避免组件移除时导致的表单项变化引起数据的变化
                                const currentParams = JSON.parse(JSON.stringify($params));
                                const oldParams = getStorageDataByKey(pageId);
                                const { effect: submitEffect, effectExcept: submitEffectExcept } = submitOptions || {};
                                const resultEffect = submitEffect || configEffect;
                                const resultEffectExcept = submitEffectExcept || configEffectExcept;

                                // 页面表单值发生变化, 清除后续内容
                                if (!lodash.isEqual(currentParams, oldParams)) {
                                    // resultEffectExcept优先级更高
                                    if (resultEffectExcept) {
                                        if (Array.isArray(resultEffectExcept)) {
                                            Object.keys(noFlatStorageData).forEach((itemKeyId: PageIdEnum) => {
                                                // 如果当前key不在resultEffectExcept中，就清除该key的数据
                                                if (!resultEffectExcept.includes(itemKeyId)) {
                                                    setStorageDataByKey(itemKeyId, {});
                                                }
                                            });
                                        } else {
                                            Object.keys(noFlatStorageData).forEach((itemKeyId: PageIdEnum) => {
                                                if (resultEffectExcept !== itemKeyId) {
                                                    setStorageDataByKey(itemKeyId, {});
                                                }
                                            });
                                        }
                                        // 如果有受影响的模块, 就清理掉
                                    } else if (resultEffect) {
                                        // 将需要清除的模块的数据清空
                                        if (Array.isArray(resultEffect)) {
                                            resultEffect.forEach(itemId => setStorageDataByKey(itemId, {}));
                                        } else {
                                            setStorageDataByKey(resultEffect, {});
                                        }
                                    }

                                    if (cache) {
                                        setStorageDataByKey(pageId, $params);
                                    }
                                }

                                if (typeof next === 'function') {
                                    try {
                                        await next();
                                    } catch {}
                                } else {
                                    navigate({
                                        ...location,
                                        pathname: next
                                    });
                                }
                            }
                        };

                        return (
                            <>
                                <ScrollTop />
                                <WrappedComponent
                                    {...transferStepProps}
                                    $formutil={$formutil}
                                    onSubmit={handleSubmit}
                                    goNextPath={goNextPath}
                                    getStorageDataByKey={getStorageDataByKey}
                                    clearStorageData={clearStorageData}
                                    setStorageDataByKey={setStorageDataByKey}
                                />
                                <Debug data={$formutil.$params} title="$params" />
                                <Debug data={$formutil.$errors} title="$errors" />
                            </>
                        );
                    }}
                />
            );
        }

        return TransferStepForm;
    };
}

export default createTransferForm;
